import { z } from 'zod';
import { createBidirectionalEnum } from '@/features/devices/schemas/devices.utils.ts';

const DEVICE_TYPES = {
  Inverter: 10,
  BMS: 20,
  SmartMeter: 30,
  SmartLogger: 40,
  Dongle: 50,
  Logger: 60,
  PQConnect: 70,
  Battery: 80,
  Sensor: 90,
  Unknown: 1000,
} as const;

export const PLANT_MEASUREMENT_METADATA = {
  'MQTT Connection Status': 4001,
} as const;

export const COMMON_DEVICE_FIELDS = {
  'ModBus Status': 1,
  'Event Status': 5001,
} as const;

export const DEVICE_TYPES_NAMES_ARRAY = Object.keys(DEVICE_TYPES) as [
  keyof typeof DEVICE_TYPES,
];

export const DEVICE_TYPES_CODES_ARRAY = Object.values(DEVICE_TYPES) as [
  (typeof DEVICE_TYPES)[keyof typeof DEVICE_TYPES],
];

const DEVICE_TYPES_MAP = createBidirectionalEnum(DEVICE_TYPES);
export const COMMON_DEVICE_FIELDS_MAP = createBidirectionalEnum(COMMON_DEVICE_FIELDS);
export const PLANT_MEASUREMENT_MAP = createBidirectionalEnum(PLANT_MEASUREMENT_METADATA);

export const DEVICE_TYPE_NAME_BY_CODE = DEVICE_TYPES_MAP.byValue;
export const DEVICE_TYPE_CODE_BY_NAME = DEVICE_TYPES_MAP.byKey;
export const DEVICE_FIELD_NAME_BY_CODE = COMMON_DEVICE_FIELDS_MAP.byValue;
export const DEVICE_FIELD_CODE_BY_NAME = COMMON_DEVICE_FIELDS_MAP.byKey;
export const PLANT_MEASUREMENT_NAME_BY_CODE = PLANT_MEASUREMENT_MAP.byValue;
export const PLANT_MEASUREMENT_CODE_BY_NAME = PLANT_MEASUREMENT_MAP.byKey;

export type DeviceTypeName = keyof typeof DEVICE_TYPE_CODE_BY_NAME;
export type DeviceTypeCodes = (typeof DEVICE_TYPE_CODE_BY_NAME)[DeviceTypeName];
export type CommonDeviceFieldName = keyof typeof DEVICE_FIELD_CODE_BY_NAME;
export type CommonDeviceFieldCodes = (typeof DEVICE_FIELD_CODE_BY_NAME)[CommonDeviceFieldName];
export type PlantMeasurementName = keyof typeof PLANT_MEASUREMENT_CODE_BY_NAME;
export type PlantMeasurementCodes =
  (typeof PLANT_MEASUREMENT_CODE_BY_NAME)[PlantMeasurementName];

export const deviceTypeNameEnum = z.enum(DEVICE_TYPES_NAMES_ARRAY, {
  error: 'Device type is required',
});
export type DeviceTypeNames = z.infer<typeof deviceTypeNameEnum>;

export const deviceTypeIdEnum = z.enum(DEVICE_TYPES_NAMES_ARRAY, {
  error: 'Device type is required',
});
export type DeviceTypeIds = z.infer<typeof deviceTypeIdEnum>;

export const DeviceSchema = z.object({
  companyId: z.string({
    error: 'Company ID is required',
  }),
  plantId: z.string({
    error: 'Plant ID is required',
  }),
  deviceId: z.string({
    error: 'Device ID is required',
  }),

  //
  name: z
    .string({
      error: 'Device name is required',
    })
    .min(3, {
      message: 'Device name must be at least 3 characters long',
    }),
  SN: z
    .string({
      error: 'Serial number is required',
    })
    .min(3, {
      message: 'Serial number must be at least 3 characters long',
    }),
  type: deviceTypeNameEnum.describe('Device type'),
  model: z
    .string({
      error: 'Device model is required',
    })
    .transform((val) => val ?? 'Unknown')
    .default('Unknown'),
  supplier: z
    .string({
      error: 'Supplier name is required',
    })
    .transform((val) => val ?? 'Unknown')
    .default('Unknown'),

  //
  showInDashboard: z
    .boolean({
      error: 'Show in dashboard is required',
    })
    .transform((val) => val ?? false)
    .default(false),
  timezone: z.string({
    error: 'Timezone is required',
  }),

  //
  connectedAt: z
    .string({
      error: 'Connected at is required',
    })
    .transform((val) => val ?? '')
    .default(''),
  updatedAt: z
    .string({
      error: 'Updated at is required',
    })
    .transform((val) => val ?? new Date().toISOString())
    .default(new Date().toISOString()),
  createdAt: z
    .string({
      error: 'Created at is required',
    })
    .transform((val) => val ?? new Date().toISOString())
    .default(new Date().toISOString()),
});

export type TDevice = z.infer<typeof DeviceSchema>;
export type TDevices = z.infer<typeof DeviceSchema>[];

export type TTreeDevice = z.input<typeof DeviceSchema> & {
  subDevices?: TTreeDevice[];
};

export const TreeDeviceSchema: z.ZodType<TTreeDevice> = DeviceSchema.extend({
  subDevices: z.lazy(() => TreeDeviceSchema.array()).optional(),
});

export const createDefaultDevice = ({
  companyId,
  plantId,
  timezone,
}: {
  companyId: string;
  plantId: string;
  timezone: string;
}): TDevice => {
  return {
    companyId,
    plantId,
    deviceId: '',

    name: '',
    SN: '',
    type: 'Unknown',
    model: 'Unknown',
    supplier: 'Unknown',

    showInDashboard: false,
    timezone,

    connectedAt: '',
    updatedAt: '',
    createdAt: '',
  };
};
